// RiskLab — esercizio EDUCATIVO sulla tolleranza al rischio.
// Non è un questionario di adeguatezza: nessun dato trasmesso/salvato, nessuna raccomandazione
// personalizzata. Serie storiche STILIZZATE (annuali) a solo scopo illustrativo.
// Audit quant + legale 12/07/2026 applicati:
//  - Q8 fuori dallo score (soglia = vincolo indipendente, niente auto-convalida)
//  - coherence check con gestione onesta del caso "nessun mix entro soglia" (tol 5/15%)
//  - durata dell'episodio del MDD con flag censura; etichette "chiusure annuali"
//  - wording: 2ª persona SOLO sugli input; output sempre su "profilo-tipo" astratto;
//    niente lessico di idoneità ("compatibile", "coerente", "il tuo profilo");
//    nessun parametro-profilo negli eventi analytics; niente nomi di indici riconoscibili.
import React, { useState, useMemo, useContext, useRef } from "react";
import { Line } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import { LangContext } from "../lang/LangProvider";
import { useAnalytics } from "../hooks/useAnalytics";
import "./RiskLab.css";

Chart.register(...registerables);

function L(lang, it, en) { return lang === "en" ? en : it; }

/* ── Serie annuali STILIZZATE 2000-2024: ampi panieri azionario internazionale (in EUR) e
      obbligazionario aggregato in euro; rendimenti nominali, lordi di costi e imposte,
      ribilanciamento annuale. Ordini di grandezza storici, non dati ufficiali. ── */
const YEARS = Array.from({ length: 25 }, (_, i) => 2000 + i);
const EQ = [-8, -13, -32, 11, 6, 26, 7, -2, -37, 26, 19, -2, 14, 21, 19, 10, 10, 7, -4, 30, 6, 31, -13, 19, 26];
const BD = [7, 6, 9, 4, 7, 5, 0, 2, 6, 7, 2, 3, 11, 2, 11, 1, 3, 1, 0, 6, 4, -3, -17, 7, 3];

/* Profili-TIPO didattici (archetipi predefiniti): il risultato dice a quale ASSOMIGLIANO le
   risposte, non "chi sei". Bande sullo score Q1-Q7 (0-21); Q8 resta fuori dal punteggio. */
const PROFILES = [
  { eq: 10, max: 4,  it: "Difensivo",  en: "Defensive"  },
  { eq: 30, max: 8,  it: "Prudente",   en: "Cautious"   },
  { eq: 50, max: 13, it: "Bilanciato", en: "Balanced"   },
  { eq: 70, max: 17, it: "Dinamico",   en: "Dynamic"    },
  { eq: 90, max: 21, it: "Aggressivo", en: "Aggressive" },
];
const profileFor = (s) => PROFILES.find((p) => s <= p.max) || PROFILES[PROFILES.length - 1];
const TOL = [5, 15, 30, 45]; // soglia indicata in Q8 (%); 45 = proxy di "oltre −40%"

const QUESTIONS = [
  {
    it: "Per quanto tempo il capitale dell'esercizio può restare investito senza essere toccato?",
    en: "For how long could the capital in this exercise stay invested untouched?",
    o: [
      { it: "Meno di 3 anni", en: "Less than 3 years" },
      { it: "3–5 anni", en: "3–5 years" },
      { it: "5–10 anni", en: "5–10 years" },
      { it: "Oltre 10 anni", en: "More than 10 years" },
    ],
  },
  {
    it: "Qual è l'obiettivo principale di questo capitale, nell'esercizio?",
    en: "What is the main goal of this capital, in the exercise?",
    o: [
      { it: "Preservarlo: nessuna sorpresa", en: "Preserve it: no surprises" },
      { it: "Un reddito il più stabile possibile", en: "As stable an income as possible" },
      { it: "Crescita bilanciata nel tempo", en: "Balanced growth over time" },
      { it: "Massima crescita, accettando le oscillazioni", en: "Maximum growth, accepting swings" },
    ],
  },
  {
    it: "Il portafoglio dell'esercizio perde il 20% in sei mesi. Reazione più probabile?",
    en: "The exercise portfolio drops 20% in six months. Most likely reaction?",
    o: [
      { it: "Vendere tutto", en: "Sell everything" },
      { it: "Ridurre l'esposizione", en: "Reduce exposure" },
      { it: "Tenere e aspettare", en: "Hold and wait" },
      { it: "Comprare ancora", en: "Buy more" },
    ],
  },
  {
    it: "Esperienza con gli investimenti?",
    en: "Experience with investing?",
    o: [
      { it: "Nessuna", en: "None" },
      { it: "Fondi o ETF, cose semplici", en: "Funds or ETFs, simple things" },
      { it: "Anche azioni singole", en: "Individual stocks too" },
      { it: "Anche strumenti complessi", en: "Complex instruments too" },
    ],
  },
  {
    it: "Quale quota del patrimonio complessivo investiresti, in ipotesi, in un portafoglio di lungo periodo come quelli di questo esercizio?",
    en: "What share of total wealth would you hypothetically invest in a long-term portfolio like the ones in this exercise?",
    o: [
      { it: "Meno del 10%", en: "Less than 10%" },
      { it: "10–25%", en: "10–25%" },
      { it: "25–50%", en: "25–50%" },
      { it: "Oltre il 50%", en: "More than 50%" },
    ],
  },
  {
    it: "Stabilità del reddito?",
    en: "Income stability?",
    o: [
      { it: "Precario o discontinuo", en: "Precarious or irregular" },
      { it: "Variabile", en: "Variable" },
      { it: "Stabile", en: "Stable" },
      { it: "Molto stabile, con riserve", en: "Very stable, with reserves" },
    ],
  },
  {
    it: "“So spiegare cos'è la diversificazione”: quanto è vero?",
    en: "“I can explain what diversification is”: how true is that?",
    o: [
      { it: "Per niente", en: "Not at all" },
      { it: "Idea vaga", en: "Vague idea" },
      { it: "Abbastanza vero", en: "Fairly true" },
      { it: "La spiegherei a un amico", en: "I could teach a friend" },
    ],
  },
  {
    // Q8: NON entra nello score — fissa solo la soglia del confronto storico.
    it: "La caduta massima sopportabile senza perdere il sonno, nell'esercizio?",
    en: "The maximum drop bearable without losing sleep, in the exercise?",
    o: [
      { it: "Circa −5%", en: "About −5%" },
      { it: "Circa −15%", en: "About −15%" },
      { it: "Circa −30%", en: "About −30%" },
      { it: "Oltre −40%", en: "Beyond −40%" },
    ],
  },
];

/* ── motore: simulazione storica del mix (10.000 € iniziali, chiusure annuali) ── */
function simulate(eqPct) {
  const w = eqPct / 100;
  const vals = [10000];
  const rets = [];
  let v = 10000;
  for (let i = 0; i < YEARS.length; i++) {
    const r = (w * EQ[i] + (1 - w) * BD[i]) / 100;
    rets.push(r);
    v *= 1 + r;
    vals.push(v);
  }
  // MDD su chiusure annuali + durata dell'EPISODIO del MDD (dal picco al recupero di QUEL picco)
  const peaks = [vals[0]];
  let peak = vals[0], peakIdx = 0, mdd = 0, mddPeakIdx = 0, mddTroughIdx = 0;
  for (let i = 1; i < vals.length; i++) {
    if (vals[i] >= peak) { peak = vals[i]; peakIdx = i; }
    else {
      const dd = (vals[i] - peak) / peak;
      if (dd < mdd) { mdd = dd; mddPeakIdx = peakIdx; mddTroughIdx = i; }
    }
    peaks.push(peak);
  }
  let recoverIdx = -1;
  for (let i = mddTroughIdx + 1; i < vals.length; i++) {
    if (vals[i] >= vals[mddPeakIdx]) { recoverIdx = i; break; }
  }
  const mddYears = recoverIdx >= 0 ? recoverIdx - mddPeakIdx : vals.length - 1 - mddPeakIdx;
  const mddCensored = recoverIdx < 0 && mdd < 0;
  const n = YEARS.length;
  const cagr = Math.pow(vals[n] / 10000, 1 / n) - 1;
  const mean = rets.reduce((a, b) => a + b, 0) / n;
  const vol = Math.sqrt(rets.reduce((a, b) => a + (b - mean) ** 2, 0) / (n - 1));
  const worst = Math.min(...rets);
  const wyear = YEARS[rets.indexOf(worst)];
  return { vals, peaks, cagr, vol, mdd, mddYears, mddCensored, worst, wyear, final: vals[n] };
}

export default function RiskLab() {
  const { lang } = useContext(LangContext);
  const { trackToolStart, trackRiskLabCompleted, trackCTAClick } = useAnalytics();
  const [phase, setPhase] = useState("intro"); // intro | quiz | results
  const [qi, setQi] = useState(0);
  const [score, setScore] = useState(0); // solo Q1-Q7 (0-21)
  const [tol, setTol] = useState(15);
  const [shown, setShown] = useState(null);
  const [baseProfile, setBaseProfile] = useState(null);
  const startedRef = useRef(false);

  const eur = (x) => x.toLocaleString(lang === "en" ? "en-GB" : "it-IT", { maximumFractionDigits: 0 }) + " €";
  const pct = (x) => (x * 100).toLocaleString(lang === "en" ? "en-GB" : "it-IT", { maximumFractionDigits: 1 }) + "%";

  const start = () => {
    if (!startedRef.current) { startedRef.current = true; trackToolStart("risk_lab", lang); }
    setPhase("quiz"); setQi(0); setScore(0); setTol(15); setShown(null); setBaseProfile(null);
  };

  const answer = (k) => {
    if (qi === 7) {
      const t = TOL[k];
      const p = profileFor(score);
      setTol(t); setBaseProfile(p); setShown(p); setPhase("results");
      // Nessun parametro personale (né profilo né soglia): solo il completamento dell'esercizio.
      if (trackRiskLabCompleted) trackRiskLabCompleted(lang);
      return;
    }
    setScore(score + k);
    setQi(qi + 1);
  };

  const sim = useMemo(() => (shown ? simulate(shown.eq) : null), [shown]);
  const tension = sim && shown === baseProfile && Math.abs(sim.mdd * 100) > tol;
  // mix rimasto entro soglia (chiusure annuali): il più esposto ad azioni; può NON esistere
  const withinTol = useMemo(() => {
    if (!tension) return null;
    const ok = PROFILES.filter((p) => Math.abs(simulate(p.eq).mdd * 100) <= tol);
    return ok.length ? ok.reduce((a, b) => (b.eq > a.eq ? b : a)) : null;
  }, [tension, tol]);
  const bestCase = useMemo(() => {
    const all = PROFILES.map((p) => ({ p, m: simulate(p.eq).mdd }));
    return all.reduce((a, b) => (b.m > a.m ? b : a));
  }, []);

  const chartData = useMemo(() => {
    if (!sim) return null;
    return {
      labels: ["", ...YEARS.map(String)],
      datasets: [
        { label: L(lang, "Massimo precedente", "Prior peak"), data: sim.peaks, borderColor: "rgba(148,163,184,0.55)", borderWidth: 1, borderDash: [4, 4], pointRadius: 0, fill: false },
        {
          label: L(lang, "Simulazione didattica: 10.000 € nel mix (2000→2024)", "Teaching simulation: €10,000 in the mix (2000→2024)"),
          data: sim.vals, borderColor: "#0a1f3d", backgroundColor: "rgba(201,80,60,0.12)",
          borderWidth: 2.4, pointRadius: 0, fill: "-1",
        },
      ],
    };
  }, [sim, lang]);

  const chartOpts = useMemo(() => ({
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { labels: { boxWidth: 18, font: { size: 11 } } },
      tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${eur(c.parsed.y)}` } },
    },
    scales: {
      y: { ticks: { callback: (v) => Math.round(v / 1000) + "k" } },
      x: { ticks: { maxTicksLimit: 13 } },
    },
  }), [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const title = L(lang, "Risk Lab — un esercizio per capire la tolleranza al rischio", "Risk Lab — an exercise to understand risk tolerance");
  const notice = L(
    lang,
    "Esercizio esclusivamente educativo. Le 8 domande riprendono la logica dei questionari di profilazione che la normativa impone a banche e consulenti, ma questo non è un questionario di adeguatezza e non ne sostituisce uno: non considera la situazione patrimoniale complessiva, gli obiettivi reali né la capacità di sostenere perdite. Il risultato è un profilo-tipo didattico, non una raccomandazione. Le risposte e il risultato non vengono trasmessi né salvati: l'elaborazione avviene solo nel tuo browser (il sito rileva statistiche d'uso aggregate come da cookie policy). Le serie storiche sono stilizzate a fini illustrativi e non rappresentano prodotti reali.",
    "A purely educational exercise. The 8 questions mirror the logic of the profiling questionnaires regulation requires of banks and advisers, but this is NOT a suitability questionnaire and does not replace one: it does not consider overall wealth, real goals or the capacity to bear losses. The result is a teaching profile-type, not a recommendation. Answers and results are neither transmitted nor stored: everything runs in your browser (the site collects aggregate usage statistics as per the cookie policy). Historical series are stylised for illustration and do not represent real products."
  );

  const mddDurText = sim && (sim.mddCensored
    ? L(lang, `a fine serie quel massimo non era ancora stato recuperato (episodio ancora aperto dopo ${sim.mddYears} anni)`,
        `by the end of the series that peak had not yet been recovered (episode still open after ${sim.mddYears} years)`)
    : L(lang, `e il massimo precedente è stato superato solo dopo circa ${sim.mddYears} anni`,
        `and the prior peak was only exceeded after about ${sim.mddYears} years`));

  return (
    <div className="rlab">
      <h2 className="rlab-title">{title}</h2>
      <p className="rlab-notice">⚖️ {notice}</p>

      {phase === "intro" && (
        <div className="rlab-card">
          <p className="rlab-lead">
            {L(lang,
              "Come fa la finanza a misurare quanta perdita una persona può sopportare? Questo esercizio lo mostra dall'interno: sette domande producono un punteggio e un profilo-tipo didattico; l'ottava fissa una soglia di sopportazione; poi il profilo-tipo viene messo a confronto con 25 anni di storia dei mercati — caduta massima inclusa. Cosa NON è: una profilazione reale, una consulenza, una proposta di investimento.",
              "How does finance measure how much loss a person can bear? This exercise shows the logic from the inside: seven questions produce a score and a teaching profile-type; the eighth sets a tolerance threshold; the profile-type is then compared with 25 years of market history — maximum drawdown included. What it is NOT: an actual profiling, advice, or an investment proposal.")}
          </p>
          <button className="rlab-btn" onClick={start}>
            {L(lang, "Prova l'esercizio (2 minuti, nulla viene salvato)", "Try the exercise (2 minutes, nothing is stored)")}
          </button>
        </div>
      )}

      {phase === "quiz" && (
        <div className="rlab-card">
          <div className="rlab-qnum">{L(lang, "Domanda", "Question")} {qi + 1} / {QUESTIONS.length}</div>
          <div className="rlab-qtext">{L(lang, QUESTIONS[qi].it, QUESTIONS[qi].en)}</div>
          <div className="rlab-opts">
            {QUESTIONS[qi].o.map((o, k) => (
              <button key={k} className="rlab-opt" onClick={() => answer(k)}>
                {L(lang, o.it, o.en)}
              </button>
            ))}
          </div>
          <div className="rlab-progress"><i style={{ width: `${(qi / QUESTIONS.length) * 100}%` }} /></div>
        </div>
      )}

      {phase === "results" && sim && (
        <div className="rlab-card">
          <div className="rlab-profile-head">
            <div>
              <div className="rlab-qnum">{L(lang, "Esito dell'esercizio: il profilo-tipo più vicino alle risposte", "Exercise outcome: the profile-type closest to the answers")}</div>
              <div className="rlab-profile-name">{L(lang, shown.it, shown.en)}</div>
            </div>
            <div className="rlab-mix">
              <span className="rlab-mix-label">{L(lang, "Mix didattico di riferimento", "Teaching reference mix")}</span>
              {shown.eq}% {L(lang, "azioni", "equities")} / {100 - shown.eq}% {L(lang, "obbligazioni", "bonds")}
              <span className="rlab-mix-note">{L(lang, "Allocazione usata solo per illustrare questo profilo-tipo. Non è una proposta di investimento.", "Allocation used only to illustrate this profile-type. Not an investment proposal.")}</span>
            </div>
          </div>

          <div className="rlab-chips">
            {[
              [L(lang, "Crescita media/anno", "Avg growth/yr"), pct(sim.cagr), "pos"],
              [L(lang, "Volatilità (dev. std annua)", "Volatility (annual st.dev.)"), pct(sim.vol), ""],
              [L(lang, "Caduta massima (chiusure annuali)*", "Max drawdown (annual closes)*"), pct(sim.mdd), "neg"],
              [L(lang, "Anno peggiore", "Worst year"), `${pct(sim.worst)} (${sim.wyear})`, "neg"],
              [L(lang, "Anni per rivedere il massimo", "Years to reclaim the peak"), sim.mddCensored ? `>${sim.mddYears}†` : String(sim.mddYears), ""],
              ["10.000 € →", eur(sim.final), "pos"],
            ].map(([k, v, c]) => (
              <div key={k} className="rlab-chip"><div className="k">{k}</div><div className={`v ${c}`}>{v}</div></div>
            ))}
          </div>

          <div className="rlab-chart"><Line data={chartData} options={chartOpts} /></div>
          <p className="rlab-chart-note">
            {L(lang,
              "*Finestra 2000-2024: parte dal picco della bolla dot-com — una delle peggiori “porte d'ingresso” possibili per l'azionario (subito −45% nel 2000-02 e −37% nel 2008). Per questo le crescite medie annue appaiono modeste e l'azionario stacca poco le obbligazioni: è il “decennio perduto”. In una finestra diversa (es. dal 2010) i numeri sarebbero molto più alti — ed è proprio la lezione: il momento di partenza conta enormemente. Su chiusure annuali la caduta reale infra-annuale è più profonda (un 50/50 nel 2008-09 toccò circa −25/−30% su dati mensili; il ~−25% di marzo 2020 qui non appare). † = episodio non ancora recuperato a fine serie. Serie stilizzate, arrotondate.",
              "*Window 2000-2024: it starts at the dot-com bubble peak — one of the worst possible entry points for equities (−45% in 2000-02, −37% in 2008 right away). That's why average annual growth looks modest and equities barely beat bonds: it's the “lost decade”. In a different window (e.g. from 2010) the numbers would be far higher — which is exactly the lesson: the starting point matters enormously. On annual closes the true intra-year drawdown is deeper (a 50/50 mix hit about −25/−30% on monthly data in 2008-09; the ~−25% of March 2020 doesn't show). † = episode not yet recovered at the end of the series. Stylised, rounded series.")}
          </p>

          {tension && withinTol && (
            <div className="rlab-verdict ko">
              <h3>{L(lang, "Da notare: la soglia indicata e la storia di questo mix raccontano due cose diverse", "Worth noting: the threshold you set and this mix's history tell two different stories")}</h3>
              <p>
                {L(lang,
                  `Alla domanda 8 la soglia indicata è circa −${tol}%. Nella serie storica stilizzata questo mix è arrivato a ${pct(sim.mdd)} — 10.000 € diventati ${eur(10000 * (1 + sim.mdd))} al minimo — ${mddDurText}. La domanda che l'esercizio lascia aperta: davanti a quei numeri, tenere o vendere sul fondo? Per confronto: nella stessa serie, il profilo-tipo rimasto entro −${tol}% (su chiusure annuali) è ${withinTol.it} (${withinTol.eq}/${100 - withinTol.eq}). Metterli fianco a fianco mostra il prezzo del minor rischio: cadute più contenute, crescita più bassa. Nessuno dei due è un consiglio: è un confronto tra numeri storici, non una valutazione di adeguatezza.`,
                  `At question 8 the threshold set is about −${tol}%. In the stylised historical series this mix reached ${pct(sim.mdd)} — €10,000 became ${eur(10000 * (1 + sim.mdd))} at the trough — ${mddDurText}. The question the exercise leaves open: facing those numbers, hold or sell at the bottom? For comparison: in the same series, the profile-type that stayed within −${tol}% (on annual closes) is ${withinTol.en} (${withinTol.eq}/${100 - withinTol.eq}). Side by side they show the price of lower risk: shallower falls, lower growth. Neither is advice: it is a comparison of historical numbers, not a suitability assessment.`)}
              </p>
              <button className="rlab-btn ghost" onClick={() => setShown(withinTol)}>
                {L(lang, `Confronta con il profilo-tipo ${withinTol.it} (${withinTol.eq}/${100 - withinTol.eq})`, `Compare with the ${withinTol.en} profile-type (${withinTol.eq}/${100 - withinTol.eq})`)}
              </button>
            </div>
          )}
          {tension && !withinTol && (
            <div className="rlab-verdict ko">
              <h3>{L(lang, "La lezione più importante di questo esercizio", "The most important lesson of this exercise")}</h3>
              <p>
                {L(lang,
                  `Alla domanda 8 la soglia indicata è circa −${tol}%. Su questi 25 anni NESSUN mix illustrativo, nemmeno il più difensivo, è rimasto entro quella soglia sulle chiusure annuali: il caso migliore è ${bestCase.p.it} (${bestCase.p.eq}/${100 - bestCase.p.eq}) con ${pct(bestCase.m)} — nel 2022 anche le obbligazioni hanno perso (circa −17% l'aggregato euro). La storia osservata dice che investire in questi mix ha sempre comportato, prima o poi, cadute oltre una soglia così bassa: è esattamente il conflitto che una valutazione di adeguatezza serve a far emergere. Nessun consiglio: è ciò che mostrano i numeri.`,
                  `At question 8 the threshold set is about −${tol}%. Over these 25 years NO illustrative mix, not even the most defensive, stayed within that threshold on annual closes: the best case is ${bestCase.p.en} (${bestCase.p.eq}/${100 - bestCase.p.eq}) at ${pct(bestCase.m)} — in 2022 even bonds lost money (euro aggregate about −17%). Observed history says investing in these mixes always involved, sooner or later, drops beyond such a low threshold: exactly the conflict a suitability assessment is meant to surface. No advice: it is what the numbers show.`)}
              </p>
            </div>
          )}
          {!tension && shown === baseProfile && (
            <div className="rlab-verdict ok">
              <h3>{L(lang, "Soglia indicata e storia del mix: nessuna tensione (in questa serie)", "Threshold set and mix history: no tension (in this series)")}</h3>
              <p>
                {L(lang,
                  `La caduta massima storica del mix didattico (${pct(sim.mdd)}, su chiusure annuali) non ha superato la soglia indicata alla domanda 8 (−${tol}%). Promemoria: infra-anno le cadute sono più profonde, e i rendimenti passati non sono indicativi di quelli futuri.`,
                  `The historical maximum drawdown of the teaching mix (${pct(sim.mdd)}, on annual closes) did not exceed the threshold set at question 8 (−${tol}%). Reminder: intra-year falls run deeper, and past returns are not indicative of future ones.`)}
              </p>
            </div>
          )}
          {shown !== baseProfile && (
            <div className="rlab-verdict ok">
              <h3>{L(lang, "Confronto: nella serie 2000-2024 questo mix è rimasto entro la soglia indicata", "Comparison: in the 2000-2024 series this mix stayed within the set threshold")}</h3>
              <p>
                {L(lang,
                  `Nella serie storica stilizzata, la caduta massima del profilo-tipo ${shown.it} (${pct(sim.mdd)}, chiusure annuali) non ha superato la soglia indicata alla domanda 8 (−${tol}%). Il prezzo: crescita più bassa (10.000 € → ${eur(sim.final)}). È un confronto tra numeri storici, non una valutazione di adeguatezza.`,
                  `In the stylised historical series, the maximum drawdown of the ${shown.en} profile-type (${pct(sim.mdd)}, annual closes) did not exceed the threshold set at question 8 (−${tol}%). The price: lower growth (€10,000 → ${eur(sim.final)}). It is a comparison of historical numbers, not a suitability assessment.`)}
              </p>
              <button className="rlab-btn ghost" onClick={() => setShown(baseProfile)}>
                {L(lang, "Torna al profilo-tipo dell'esercizio", "Back to the exercise profile-type")}
              </button>
            </div>
          )}

          {/* Mappa dei 5 profili-tipo: trasforma il risultato in lezione sullo spettro rischio/rendimento */}
          <div className="rlab-map">
            <h4>{L(lang, "La mappa completa dei 5 profili-tipo (stessa serie storica)", "The full map of the 5 profile-types (same historical series)")}</h4>
            <div className="rlab-map-scroll">
              <table>
                <thead>
                  <tr>
                    <th>{L(lang, "Profilo-tipo", "Profile-type")}</th>
                    <th>{L(lang, "Mix", "Mix")}</th>
                    <th>{L(lang, "Crescita/anno", "Growth/yr")}</th>
                    <th>{L(lang, "Caduta max*", "Max drawdown*")}</th>
                    <th>10.000 € →</th>
                  </tr>
                </thead>
                <tbody>
                  {PROFILES.map((p) => {
                    const s = simulate(p.eq);
                    return (
                      <tr key={p.eq} className={p === shown ? "is-shown" : ""}>
                        <td>{L(lang, p.it, p.en)}</td>
                        <td>{p.eq}/{100 - p.eq}</td>
                        <td>{pct(s.cagr)}</td>
                        <td className="neg">{pct(s.mdd)}</td>
                        <td>{eur(s.final)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rlab-row">
            <button className="rlab-btn ghost" onClick={start}>{L(lang, "Rifai l'esercizio", "Redo the exercise")}</button>
            <a
              className="rlab-btn"
              href={lang === "en" ? "/en/kb/risk-tolerance-what-a-questionnaire-really-measures" : "/it/kb/tolleranza-al-rischio-cosa-misura-un-questionario"}
              onClick={() => trackCTAClick && trackCTAClick("risk_lab_article", "kb_article", "POST")}
            >
              {L(lang, "Come funziona questo esercizio (articolo)", "How this exercise works (article)")}
            </a>
          </div>

          <p className="rlab-fine">
            {L(lang,
              "Serie annuali stilizzate (ampi panieri azionario internazionale e obbligazionario in euro, 2000-2024): non dati ufficiali né rendimenti di prodotti reali; valori al lordo di costi, imposte e inflazione. I rendimenti passati non sono indicativi di quelli futuri. I 5 profili-tipo sono categorie didattiche e non equivalgono alla profilazione di un intermediario. Questo esercizio non costituisce consulenza in materia di investimenti, non produce raccomandazioni personalizzate e non instaura alcun rapporto professionale. Per decisioni reali serve una valutazione di adeguatezza completa presso un soggetto autorizzato.",
              "Stylised annual series (broad international equity and euro bond baskets, 2000-2024): not official data nor returns of real products; figures gross of costs, taxes and inflation. Past returns are not indicative of future ones. The 5 profile-types are teaching categories and do not equate to an intermediary's profiling. This exercise is not investment advice, produces no personalised recommendations and establishes no professional relationship. Real decisions require a full suitability assessment with an authorised provider.")}
          </p>
        </div>
      )}
    </div>
  );
}
